/**
 * ConfirmDialog — Bestätigungs-Dialog auf Modal-Basis (Sprint UI-D2a).
 *
 * `role="alertdialog"`, Footer aus zwei `Button`-Primitives. Initial-Fokus
 * liegt auf Cancel (destruktive Bestätigung nicht versehentlich per Enter).
 * `loading` reicht `disableClose` an `Modal` weiter → X/Esc/Backdrop gesperrt
 * solange die async-Aktion läuft, Cancel ist `disabled`, Confirm zeigt Spinner.
 */
import { useId, useRef, type ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';

export type ConfirmVariant = 'default' | 'danger';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** Einfache Text-Beschreibung — wird via `aria-describedby` verknüpft. */
  message?: string;
  /** Reicher Inhalt (Listen, Error-Banner) — alternativ/zusätzlich zu `message`. */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  children,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  variant = 'default',
  loading = false,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const descId = `gf-confirm-desc-${useId()}`;
  const hasMessage = typeof message === 'string' && message.length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      role="alertdialog"
      size="sm"
      initialFocusRef={cancelRef}
      disableClose={loading}
      describedById={hasMessage ? descId : undefined}
      footer={
        <>
          <Button
            ref={cancelRef}
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {hasMessage && (
        <p id={descId} className="text-sm text-brand-green-800">
          {message}
        </p>
      )}
      {children}
    </Modal>
  );
}
